/**
 * GDriveFolderPicker
 *
 * A modal destination picker for Google Drive. Opens via a popup OAuth window
 * when the user is not yet connected, then walks the user through:
 *   1. Location  — Iowa / Minnesota / Colorado
 *      (maps to My Drive / Infinity / *Patients / {State})
 *   2. Permissions — Pre-Approved / Check Before Posting / Private + Internal Use Only
 *      (selects the matching subfolder inside the State folder)
 *   3. (Optional, only for Pre-Approved or Check Before Posting) Special
 *      Instructions — routes uploads into a "**Special Instructions" subfolder.
 *
 * The chosen Permission also drives the Google Drive folder color applied to
 * the new patient folder (green / blue / red).
 */
import { useState, useEffect, useCallback, useRef } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Loader2, CloudOff, Check, MapPin, Shield, AlertTriangle, Users } from "lucide-react";
import { cn } from "@/lib/utils";
import { trpc } from "@/lib/trpc";
import GoogleDriveLogo from "@/components/GoogleDriveLogo";
import { useDefaultLocation } from "@/hooks/useDefaultLocation";

// ─── Permission definitions ──────────────────────────────────────────────────

/** Permissions the user can pick from the modal. */
export type Permission =
  | "pre-approved"
  | "check-before-posting"
  | "private-internal";

/**
 * Drive folder color labels the picker can emit. Permissions map 1:1 to a
 * color; selecting the Special Instructions sub-destination upgrades the
 * label to "special-instruction" (orange) instead.
 */
export type FolderColorLabel = Permission | "special-instruction";

interface PermissionOption {
  value: Permission;
  label: string;
  /** Folder name in Drive that lives under each region. */
  folderName: string;
  /** Tailwind text color class shown in the UI */
  textClass: string;
  /** Tailwind border/ring color class for the selected ring */
  ringClass: string;
  /** Tailwind bg class for the color swatch dot */
  dotClass: string;
  /** Human-readable Drive color name shown as a tooltip */
  driveName: string;
  /** Whether the Special Instructions section can be revealed when this permission is chosen */
  allowsSpecialInstructions: boolean;
}

const PERMISSION_OPTIONS: PermissionOption[] = [
  {
    value: "pre-approved",
    label: "Pre-Approved",
    folderName: "Pre-Approved",
    textClass: "text-emerald-600 dark:text-emerald-400",
    ringClass: "ring-emerald-500",
    dotClass: "bg-emerald-500",
    driveName: "Vern fern (green)",
    allowsSpecialInstructions: true,
  },
  {
    value: "check-before-posting",
    label: "Check Before Posting",
    folderName: "Check Before Posting",
    textClass: "text-[#4986e7]",
    ringClass: "ring-[#4986e7]",
    dotClass: "bg-[#4986e7]",
    driveName: "Rainy sky (blue)",
    allowsSpecialInstructions: true,
  },
  {
    value: "private-internal",
    label: "Private + Internal Use Only",
    folderName: "Private + Internal Use Only",
    textClass: "text-red-600 dark:text-red-400",
    ringClass: "ring-red-500",
    dotClass: "bg-red-500",
    driveName: "Cardinal (red)",
    allowsSpecialInstructions: false,
  },
];

// ─── Location definitions ─────────────────────────────────────────────────────

export type FolderLocation = "Iowa" | "Minnesota" | "Colorado";

const LOCATION_OPTIONS: FolderLocation[] = ["Iowa", "Minnesota", "Colorado"];

const SPECIAL_INSTRUCTIONS_FOLDER_NAME = "**Special Instructions";

// ─── Props ────────────────────────────────────────────────────────────────────

interface GDriveFolderPickerProps {
  open: boolean;
  onClose: () => void;
  /** Optional — kept for legacy callers; no longer sent to the server. */
  userOpenId?: string;
  /** Called when the user confirms a destination selection */
  onSelect: (folderId: string, folderPath: string, colorLabel: FolderColorLabel | null) => void;
  /** Currently selected folder ID (to show a checkmark) */
  selectedFolderId?: string;
  /** Currently saved color label (pre-select on reopen) */
  selectedColorLabel?: FolderColorLabel | null;
  /** When true, a Permission must be chosen before confirming */
  requireColorLabel?: boolean;
  /**
   * What subject the picker is choosing a destination for.
   * "patient" (default) sources from *Patients and shows the Permissions section.
   * "provider" sources from *Providers, drops the Permissions section, and
   * resolves the destination to the chosen Region folder directly.
   */
  subjectKind?: "patient" | "provider";
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function GDriveFolderPicker({
  open,
  onClose,
  onSelect,
  selectedColorLabel,
  requireColorLabel = false,
  subjectKind = "patient",
}: GDriveFolderPickerProps) {
  const isProvider = subjectKind === "provider";
  const subjectRootName = isProvider ? "*Providers" : "*Patients";
  const [connecting, setConnecting] = useState(false);
  const [location, setLocation] = useState<FolderLocation | null>(null);
  const [regionFolderId, setRegionFolderId] = useState<string | null>(null);
  const [locationLoading, setLocationLoading] = useState(false);
  const initialPermission: Permission | null =
    selectedColorLabel && selectedColorLabel !== "special-instruction"
      ? selectedColorLabel
      : null;
  const [permission, setPermission] = useState<Permission | null>(initialPermission);
  const [includeSpecialInstructions, setIncludeSpecialInstructions] = useState(
    selectedColorLabel === "special-instruction",
  );
  // Provider-only: drill into a specific provider folder under the region.
  // Patient flow uses the Permission section in this slot instead.
  const [providers, setProviders] = useState<
    Array<{ name: string; folderId: string }>
  >([]);
  const [providersLoading, setProvidersLoading] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState<
    { name: string; folderId: string } | null
  >(null);
  const [resolving, setResolving] = useState(false);
  const [resolveError, setResolveError] = useState<string | null>(null);

  const utils = trpc.useUtils();

  // Check connection status
  const { data: statusData, isLoading: statusLoading, refetch: refetchStatus } =
    trpc.gdrive.status.useQuery(undefined, { enabled: open });

  const isConnected = statusData?.connected ?? false;

  // Reset state when the modal opens; restore prior permission if reopening
  useEffect(() => {
    if (open) {
      setLocation(null);
      setRegionFolderId(null);
      setPermission(
        selectedColorLabel && selectedColorLabel !== "special-instruction"
          ? selectedColorLabel
          : null,
      );
      setIncludeSpecialInstructions(selectedColorLabel === "special-instruction");
      setProviders([]);
      setSelectedProvider(null);
      setResolveError(null);
    }
  }, [open, selectedColorLabel]);

  // When the page returns from Google OAuth with ?gdrive_connected=1, refetch status
  useEffect(() => {
    if (!open) return;
    const params = new URLSearchParams(window.location.search);
    if (params.get("gdrive_connected") === "1") {
      const clean = window.location.pathname;
      window.history.replaceState({}, "", clean);
      refetchStatus();
    }
  }, [open, refetchStatus]);

  const handleConnect = useCallback((e?: React.MouseEvent) => {
    e?.preventDefault();
    e?.stopPropagation();
    setConnecting(true);
    sessionStorage.setItem("gdrive_picker_pending", "1");
    const origin = window.location.origin;
    const url = `/api/gdrive/auth?origin=${encodeURIComponent(origin)}`;
    window.location.href = url;
  }, []);

  /** Handle location selection: fetch region folder IDs and (for provider
   *  mode) the list of provider subfolders inside that region. */
  const handleLocationSelect = useCallback(
    async (loc: FolderLocation) => {
      if (loc === location) {
        setLocation(null);
        setRegionFolderId(null);
        setProviders([]);
        setSelectedProvider(null);
        setResolveError(null);
        return;
      }
      setLocation(loc);
      setRegionFolderId(null);
      setProviders([]);
      setSelectedProvider(null);
      setResolveError(null);
      setLocationLoading(true);
      try {
        const regionIds = isProvider
          ? await utils.gdrive.getProviderRegionFolderIds.fetch()
          : await utils.gdrive.getRegionFolderIds.fetch();
        const id = regionIds[loc];
        if (!id) {
          setResolveError(`Could not find the "${loc}" folder in Google Drive under ${subjectRootName}.`);
          return;
        }
        setRegionFolderId(id);
        // Provider flow: drill one more level — fetch the provider folders
        // under this region so the user can pick a specific destination.
        if (isProvider) {
          setProvidersLoading(true);
          try {
            const list = await utils.gdrive.listDriveProviders.fetch({ region: loc });
            setProviders(list.map((p) => ({ name: p.name, folderId: p.folderId })));
          } catch (err) {
            console.warn("[GDrivePicker] Could not list providers:", err);
            setResolveError("Could not load the provider list for this region.");
          } finally {
            setProvidersLoading(false);
          }
        }
      } catch (err) {
        console.warn("[GDrivePicker] Could not fetch region folder IDs:", err);
        setResolveError("Could not load Google Drive folders. Please try again.");
      } finally {
        setLocationLoading(false);
      }
    },
    [location, utils, isProvider, subjectRootName],
  );

  // Pre-select the user's home location once per open (after the reset effect
  // above clears it and once Drive is connected). Runs the full
  // handleLocationSelect so the region folder + provider list load, exactly as
  // if the user had tapped the chip. The user can still change it afterwards.
  const defaultLocation = useDefaultLocation();
  const autoPickedRef = useRef(false);
  useEffect(() => {
    if (!open) {
      autoPickedRef.current = false;
      return;
    }
    if (autoPickedRef.current) return;
    if (!isConnected) return;
    if (location !== null) return;
    if (!defaultLocation) return;
    autoPickedRef.current = true;
    void handleLocationSelect(defaultLocation);
  }, [open, isConnected, location, defaultLocation, handleLocationSelect]);

  const currentPermissionOption = permission
    ? PERMISSION_OPTIONS.find((p) => p.value === permission) ?? null
    : null;
  const specialInstructionsAvailable =
    currentPermissionOption?.allowsSpecialInstructions ?? false;

  // If the user switches to a permission that doesn't allow Special Instructions,
  // clear the toggle so we don't try to resolve a non-existent folder.
  useEffect(() => {
    if (!specialInstructionsAvailable && includeSpecialInstructions) {
      setIncludeSpecialInstructions(false);
    }
  }, [specialInstructionsAvailable, includeSpecialInstructions]);

  /** Resolve the final destination and confirm. */
  const handleConfirm = async () => {
    if (!location || !regionFolderId) return;
    if (isProvider && !selectedProvider) return;
    if (!isProvider && !permission) return;
    setResolving(true);
    setResolveError(null);
    try {
      if (isProvider && selectedProvider) {
        // Providers: destination is the specific provider folder under the
        // selected region. No permission tier and no auto-created subject
        // subfolder — files in the A-Roll flow save flat into the picked
        // local folder, but Drive still lands under the provider's folder.
        const pathSegments = [
          "My Drive",
          "Infinity",
          "*Providers",
          location,
          selectedProvider.name,
        ];
        onSelect(selectedProvider.folderId, pathSegments.join(" / "), null);
        onClose();
        return;
      }
      const result = await utils.gdrive.resolvePermissionFolder.fetch({
        regionFolderId,
        permission: permission!,
        includeSpecialInstructions,
      });
      const usingSpecialInstructions =
        includeSpecialInstructions && !!result.specialInstructionsFolderId;
      const folderId = usingSpecialInstructions
        ? result.specialInstructionsFolderId!
        : result.permissionFolderId;
      const pathSegments = [
        "My Drive",
        "Infinity",
        "*Patients",
        location,
        result.permissionFolderName,
      ];
      if (usingSpecialInstructions && result.specialInstructionsFolderName) {
        pathSegments.push(result.specialInstructionsFolderName);
      }
      // When Special Instructions is the destination, color the new patient
      // folder orange to match — otherwise use the Permission's color.
      const colorLabel: FolderColorLabel = usingSpecialInstructions
        ? "special-instruction"
        : permission!;
      onSelect(folderId, pathSegments.join(" / "), colorLabel);
      onClose();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : "Could not resolve the destination folder.";
      setResolveError(message);
    } finally {
      setResolving(false);
    }
  };

  const canConfirm =
    !!location &&
    !!regionFolderId &&
    (isProvider ? !!selectedProvider : !!permission) &&
    !locationLoading &&
    !providersLoading &&
    !resolving;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md p-0 overflow-hidden">
        <DialogHeader className="px-5 pt-5 pb-3 border-b">
          <DialogTitle className="flex items-center gap-2 text-base">
            <GoogleDriveLogo className="w-5 h-5 flex-shrink-0" />
            Google Drive Upload Destination
          </DialogTitle>
        </DialogHeader>

        {/* Not connected */}
        {!statusLoading && !isConnected && (
          <div className="flex flex-col items-center justify-center gap-4 py-12 px-6 text-center">
            <div className="w-14 h-14 rounded-full bg-muted flex items-center justify-center">
              <CloudOff className="w-7 h-7 text-muted-foreground" />
            </div>
            <div>
              <p className="font-semibold text-sm">Google Drive not connected</p>
              <p className="text-xs text-muted-foreground mt-1">
                Connect your Google account to choose a destination.
              </p>
            </div>
            <Button type="button" onClick={handleConnect} disabled={connecting} className="gap-2">
              {connecting ? (
                <><Loader2 className="w-4 h-4 animate-spin" /> Connecting…</>
              ) : (
                "Connect Google Drive"
              )}
            </Button>
          </div>
        )}

        {/* Loading status */}
        {statusLoading && (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
          </div>
        )}

        {/* Connected — Location → Permissions → optional Special Instructions */}
        {!statusLoading && isConnected && (
          <>
            {/* ── 1. Location ── */}
            <div className="px-4 pt-3 pb-3 border-b bg-muted/20">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2 flex items-center gap-1.5">
                <MapPin className="w-3.5 h-3.5" /> 1. Location
              </p>
              <div className="flex gap-2 flex-wrap">
                {LOCATION_OPTIONS.map((loc) => {
                  const isSelected = location === loc;
                  return (
                    <button
                      key={loc}
                      type="button"
                      onClick={() => handleLocationSelect(loc)}
                      disabled={locationLoading}
                      className={cn(
                        "flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs font-semibold transition-all",
                        "hover:opacity-90 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary",
                        isSelected
                          ? "border-transparent bg-primary text-primary-foreground ring-2 ring-primary"
                          : "border-border bg-background hover:bg-muted/40 text-foreground",
                      )}
                    >
                      {locationLoading && isSelected ? (
                        <Loader2 className="w-3 h-3 animate-spin" />
                      ) : isSelected ? (
                        <Check className="w-3 h-3" />
                      ) : null}
                      {loc}
                    </button>
                  );
                })}
              </div>
              {location && !locationLoading && regionFolderId && (
                <p className="text-xs text-muted-foreground mt-2">
                  Will save under{" "}
                  <span className="font-medium text-foreground">
                    My Drive / Infinity / {subjectRootName} / {location}
                  </span>
                </p>
              )}
            </div>

            {/* ── 2. Provider (providers only) ── */}
            {isProvider && (
              <div
                className={cn(
                  "px-4 pt-3 pb-3 border-b bg-muted/20 transition-opacity",
                  !location && "opacity-50 pointer-events-none",
                )}
              >
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2 flex items-center gap-1.5">
                  <Users className="w-3.5 h-3.5" /> 2. Provider
                </p>
                {providersLoading ? (
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    Loading providers in {location}…
                  </div>
                ) : !location ? (
                  <p className="text-xs text-muted-foreground">
                    Pick a Location above to see providers.
                  </p>
                ) : providers.length === 0 ? (
                  <p className="text-xs text-muted-foreground">
                    No providers found under {subjectRootName} / {location}.
                  </p>
                ) : (
                  <div className="max-h-48 overflow-y-auto flex flex-col gap-1 pr-1">
                    {providers.map((p) => {
                      const isSelected = selectedProvider?.folderId === p.folderId;
                      return (
                        <button
                          key={p.folderId}
                          type="button"
                          onClick={() =>
                            setSelectedProvider(isSelected ? null : { name: p.name, folderId: p.folderId })
                          }
                          className={cn(
                            "flex items-center justify-between gap-2 px-3 py-1.5 rounded-lg border text-xs font-medium transition-all text-left",
                            "hover:opacity-90 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary",
                            isSelected
                              ? "border-transparent bg-primary text-primary-foreground ring-2 ring-primary"
                              : "border-border bg-background hover:bg-muted/40 text-foreground",
                          )}
                        >
                          <span className="truncate">{p.name}</span>
                          {isSelected && <Check className="w-3.5 h-3.5 flex-shrink-0" />}
                        </button>
                      );
                    })}
                  </div>
                )}
                {selectedProvider && (
                  <p className="text-xs text-muted-foreground mt-2">
                    Will save under{" "}
                    <span className="font-medium text-foreground">
                      My Drive / Infinity / *Providers / {location} / {selectedProvider.name}
                    </span>
                  </p>
                )}
              </div>
            )}

            {/* ── 2. Permissions (patients only) ── */}
            {!isProvider && (
            <div
              className={cn(
                "px-4 pt-3 pb-3 border-b bg-muted/20 transition-opacity",
                !location && "opacity-50 pointer-events-none",
              )}
            >
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2 flex items-center gap-1.5">
                <Shield className="w-3.5 h-3.5" /> 2. Permissions
              </p>
              <div className="flex gap-2 flex-wrap">
                {PERMISSION_OPTIONS.map((opt) => {
                  const isSelected = permission === opt.value;
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => setPermission(isSelected ? null : opt.value)}
                      title={`Google Drive: ${opt.driveName}`}
                      className={cn(
                        "flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs font-semibold transition-all",
                        "hover:opacity-90 focus:outline-none focus-visible:ring-2",
                        isSelected
                          ? cn("border-transparent bg-muted/60 ring-2", opt.ringClass)
                          : "border-border bg-background hover:bg-muted/40",
                        opt.textClass,
                      )}
                    >
                      <span className={cn("w-2.5 h-2.5 rounded-full flex-shrink-0", opt.dotClass)} />
                      {opt.label}
                      {isSelected && <Check className="w-3 h-3 ml-0.5" />}
                    </button>
                  );
                })}
              </div>
              {permission && currentPermissionOption && (
                <p className="text-xs text-muted-foreground mt-2">
                  Saving into{" "}
                  <span className={cn("font-medium", currentPermissionOption.textClass)}>
                    {currentPermissionOption.folderName}
                  </span>
                  {" "}— the new patient folder will be colored to match in Drive.
                </p>
              )}
            </div>
            )}

            {/* ── 3. Special Instructions (optional, patients only) ── */}
            {!isProvider && specialInstructionsAvailable && (
              <div className="px-4 pt-3 pb-3 border-b bg-muted/20">
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">
                  3. Special Instructions <span className="font-normal lowercase">(optional)</span>
                </p>
                <button
                  type="button"
                  onClick={() => setIncludeSpecialInstructions((v) => !v)}
                  className={cn(
                    "flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs font-semibold transition-all",
                    "hover:opacity-90 focus:outline-none focus-visible:ring-2",
                    includeSpecialInstructions
                      ? "border-transparent bg-muted/60 ring-2 ring-[#ff7537] text-[#ff7537]"
                      : "border-border bg-background hover:bg-muted/40 text-[#ff7537]",
                  )}
                >
                  <span className="w-2.5 h-2.5 rounded-full flex-shrink-0 bg-[#ff7537]" />
                  Special Instructions
                  {includeSpecialInstructions && <Check className="w-3 h-3 ml-0.5" />}
                </button>
                {includeSpecialInstructions && (
                  <p className="text-xs text-muted-foreground mt-2">
                    Will save inside{" "}
                    <span className="font-medium text-foreground">{SPECIAL_INSTRUCTIONS_FOLDER_NAME}</span>{" "}
                    under {currentPermissionOption?.folderName}.
                  </p>
                )}
              </div>
            )}

            {/* Error banner */}
            {resolveError && (
              <div className="px-4 py-3 border-b bg-red-50 dark:bg-red-950/20">
                <p className="flex items-start gap-2 text-xs text-red-700 dark:text-red-400">
                  <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                  <span>{resolveError}</span>
                </p>
              </div>
            )}

            {/* Footer */}
            <div className="flex flex-col gap-2 px-4 py-3 bg-muted/20">
              {!isProvider && requireColorLabel && !permission && location && (
                <p className="text-xs text-amber-600 dark:text-amber-400 font-medium flex items-center gap-1">
                  <span className="inline-block w-1.5 h-1.5 rounded-full bg-amber-500" />
                  Choose a Permission before confirming.
                </p>
              )}
              <div className="flex items-center justify-between gap-2">
                <span className="text-xs text-muted-foreground">
                  {isProvider ? (
                    selectedProvider && location ? (
                      <>
                        Destination:{" "}
                        <span className="font-medium text-foreground">
                          {`*Providers / ${location} / ${selectedProvider.name}`}
                        </span>
                      </>
                    ) : location ? (
                      "Select a Provider to continue."
                    ) : (
                      "Select a Location and Provider to continue."
                    )
                  ) : location && permission ? (
                    <>
                      Destination:{" "}
                      <span className="font-medium text-foreground">
                        {`*Patients / ${location} / ${currentPermissionOption?.folderName}`}
                        {includeSpecialInstructions ? ` / ${SPECIAL_INSTRUCTIONS_FOLDER_NAME}` : ""}
                      </span>
                    </>
                  ) : (
                    "Select a Location and Permission to continue."
                  )}
                </span>
                <Button
                  size="sm"
                  onClick={handleConfirm}
                  disabled={!canConfirm}
                  className="gap-1.5 h-8 text-xs"
                >
                  {resolving ? (
                    <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Confirming…</>
                  ) : (
                    <><Check className="w-3.5 h-3.5" /> Confirm Destination</>
                  )}
                </Button>
              </div>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
